import { parseAsPageNumber } from '@shared/utils';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsIsoDateTime,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';

const TEMPLATE_VALUES = ['standard', 'longForm', 'shortForm'] as const;

export type Template = (typeof TEMPLATE_VALUES)[number];

interface FilterParams {
  examStartDate: Date | null;
  examEndDate: Date | null;
  examCenter: string[] | null;
  examCourse: string[] | null;
}

export function useQuestionnaireReportSearchParams() {
  const [
    { examStartDate, examEndDate, examCenter, examCourse, page, pageSize, template },
    setQuestionnaireReportSearchParams,
  ] = useQueryStates({
    examStartDate: parseAsIsoDateTime,
    examEndDate: parseAsIsoDateTime,
    examCenter: parseAsArrayOf(parseAsString).withDefault([]),
    examCourse: parseAsArrayOf(parseAsString).withDefault([]),
    page: parseAsPageNumber.withDefault(0),
    pageSize: parseAsInteger.withDefault(10),
    template: parseAsStringLiteral(TEMPLATE_VALUES).withDefault('standard'),
  });

  const changeFilters = (params: Partial<FilterParams>) => {
    setQuestionnaireReportSearchParams({ ...params, page: 0 });
  };

  const resetAllParams = () => {
    setQuestionnaireReportSearchParams({
      examStartDate: null,
      examEndDate: null,
      examCenter: null,
      examCourse: null,
      page: null,
      pageSize: null,
    });
  };

  const changePageNumber = (newPage: number) => {
    setQuestionnaireReportSearchParams({ page: newPage });
  };

  const changePageSize = (newSize: number) => {
    setQuestionnaireReportSearchParams({ pageSize: newSize, page: 0 });
  };

  const changeTemplate = (next: Template) => {
    setQuestionnaireReportSearchParams({ template: next });
  };

  return [
    {
      examStartDate,
      examEndDate,
      examCenter,
      examCourse,
      page,
      pageSize,
      template,
    },
    {
      changeFilters,
      resetAllParams,
      changePageNumber,
      changePageSize,
      changeTemplate,
    },
  ] as const;
}
